class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.historyLocked = false;
    this.closingViaUi = false;
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
    this.setHeaderCartIconAccessibility();
    this.bindPremiumDrawerUi();
    this.bindHistoryClose();
  }

  connectedCallback() {
    this.bindHistoryClose();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this._historyBound = false;
  }

  bindHistoryClose() {
    if (this._historyBound) return;
    this._historyBound = true;
    window.addEventListener('popstate', this.onPopState);
  }

  lockHistoryOnOpen() {
    if (this.historyLocked) return;
    try {
      history.pushState({ cartDrawerOpen: true }, '', window.location.href);
      this.historyLocked = true;
    } catch (err) {
      this.historyLocked = false;
    }
  }

  unlockHistoryOnClose() {
    if (!this.historyLocked) return;
    this.historyLocked = false;
    this.closingViaUi = true;
    try {
      history.back();
    } catch (err) {
      this.closingViaUi = false;
    }
  }

  onPopState() {
    if (this.closingViaUi) {
      this.closingViaUi = false;
      return;
    }
    if (this.classList.contains('active')) {
      this.historyLocked = false;
      this.close({ fromPopstate: true });
    }
  }

  bindPremiumDrawerUi() {
    const toggle = this.querySelector('[data-cart-drawer-note-toggle]');
    const panel = this.querySelector('[data-cart-drawer-note-panel]');
    if (!toggle || !panel || toggle.dataset.bound === 'true') return;
    toggle.dataset.bound = 'true';
    toggle.addEventListener('click', () => {
      const open = panel.hasAttribute('hidden');
      panel.toggleAttribute('hidden', !open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const alreadyOpen = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockBodyScroll();

    /* Phone back button closes drawer instead of leaving the site */
    if (!alreadyOpen) {
      this.lockHistoryOnOpen();
    }
  }

  close(options = {}) {
    const fromPopstate = Boolean(options && options.fromPopstate);
    const wasOpen = this.classList.contains('active');

    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockBodyScroll();

    if (wasOpen && !fromPopstate) {
      this.unlockHistoryOnClose();
    }
  }

  lockBodyScroll() {
    if (this._bodyScrollLocked) return;
    this._bodyScrollLocked = true;
    this._bodyScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('cart-drawer-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._bodyScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    this.bindScrollGuard();
  }

  unlockBodyScroll() {
    this.unbindScrollGuard();
    if (!this._bodyScrollLocked) {
      document.body.classList.remove('overflow-hidden');
      return;
    }
    this._bodyScrollLocked = false;
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    const y = this._bodyScrollY || 0;
    this._bodyScrollY = 0;
    window.scrollTo(0, y);
  }

  bindScrollGuard() {
    if (this._scrollGuardBound) return;
    this._scrollGuardBound = true;
    this._onTouchStartGuard = (event) => {
      const items = this.querySelector('cart-drawer-items');
      this._touchStartY = event.touches?.[0]?.clientY ?? 0;
      this._touchScrollEl = items && items.contains(event.target) ? items : null;
    };
    this._onTouchMoveGuard = (event) => {
      if (!this.classList.contains('active')) return;
      const items = this._touchScrollEl;
      if (!items) {
        event.preventDefault();
        return;
      }
      const y = event.touches?.[0]?.clientY ?? 0;
      const delta = y - (this._touchStartY || 0);
      const atTop = items.scrollTop <= 0;
      const atBottom = items.scrollTop + items.clientHeight >= items.scrollHeight - 1;
      /* Block rubber-band chaining past the drawer edges */
      if ((atTop && delta > 0) || (atBottom && delta < 0)) {
        event.preventDefault();
      }
    };
    document.addEventListener('touchstart', this._onTouchStartGuard, { passive: true, capture: true });
    document.addEventListener('touchmove', this._onTouchMoveGuard, { passive: false, capture: true });
  }

  unbindScrollGuard() {
    if (!this._scrollGuardBound) return;
    this._scrollGuardBound = false;
    document.removeEventListener('touchstart', this._onTouchStartGuard, { capture: true });
    document.removeEventListener('touchmove', this._onTouchMoveGuard, { capture: true });
    this._onTouchStartGuard = null;
    this._onTouchMoveGuard = null;
    this._touchScrollEl = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  updateContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerHtml = parsedState.sections?.['cart-drawer'];
    const itemsEl = this.querySelector('cart-drawer-items');
    if (drawerHtml && typeof itemsEl?.applyDrawerSectionHtml === 'function') {
      itemsEl.applyDrawerSectionHtml(drawerHtml);
    } else if (drawerHtml) {
      const sectionElement = document.querySelector('#CartDrawer');
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(drawerHtml, '#CartDrawer');
      }
    }

    const iconHtml = parsedState.sections?.['cart-icon-bubble'];
    if (iconHtml) {
      const iconEl = document.getElementById('cart-icon-bubble');
      if (iconEl) {
        iconEl.innerHTML = this.getSectionInnerHTML(iconHtml);
      }
    }

    const overlay = this.querySelector('#CartDrawer-Overlay');
    overlay?.addEventListener('click', this.close.bind(this));
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.bindPremiumDrawerUi();
  }

  isAutoOpenEnabled() {
    return Boolean(window.themeSettings?.cartDrawerAutoOpen);
  }

  renderContents(parsedState) {
    this.updateContents(parsedState);

    /* Theme setting: only auto-open drawer after add-to-cart when enabled */
    if (!this.isAutoOpenEnabled()) return;

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    /** @type {Record<string, number>} */
    this.desiredQuantityByLine = {};

    /* Optimistic price on every tap — do not wait for debounce / network */
    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.id || !input.id.startsWith('Drawer-quantity-')) return;

      const line = input.dataset.index;
      const qty = parseInt(input.value, 10);
      if (!line || !Number.isFinite(qty)) return;

      this.desiredQuantityByLine[String(line)] = qty;
      if (qty > 0) this.optimisticUpdateLine(line, qty);
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * Patch only the moving parts of the open drawer.
   * Avoids wiping the whole .drawer__inner (scroll jump + expensive rebuild).
   */
  applyDrawerSectionHtml(sectionHtml) {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const scrollTop = this.scrollTop;
    const noteEl = document.getElementById('CartDrawer-Note');
    const noteValue = noteEl ? noteEl.value : null;
    const noteOpen = !document.querySelector('[data-cart-drawer-note-panel]')?.hasAttribute('hidden');

    const sourceItems = doc.querySelector('cart-drawer-items');
    if (sourceItems) {
      this.innerHTML = sourceItems.innerHTML;
      this.classList.toggle('is-empty', sourceItems.classList.contains('is-empty'));
    }

    const pairs = [
      ['.drawer__footer', 'cart-drawer .drawer__footer'],
      ['.drawer__heading', 'cart-drawer .drawer__heading'],
    ];

    pairs.forEach(([sourceSel, targetSel]) => {
      const source = doc.querySelector(sourceSel);
      const target = document.querySelector(targetSel);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const drawer = document.querySelector('cart-drawer');
    const drawerInner = drawer?.querySelector('.drawer__inner');
    if (drawer && drawerInner) {
      const emptySource = doc.querySelector('.drawer__inner-empty');
      let emptyTarget = drawer.querySelector('.drawer__inner-empty');

      if (emptySource) {
        if (emptyTarget) {
          emptyTarget.innerHTML = emptySource.innerHTML;
        } else {
          const header = drawerInner.querySelector('.drawer__header');
          emptyTarget = emptySource.cloneNode(true);
          if (header) header.before(emptyTarget);
          else drawerInner.prepend(emptyTarget);
        }
      }

      const itemCountText = doc.querySelector('.drawer__heading span');
      const isEmptyCart =
        sourceItems?.classList.contains('is-empty') ||
        (itemCountText && /\(\s*0\s*\)/.test(itemCountText.textContent || ''));

      drawer.classList.toggle('is-empty', Boolean(isEmptyCart));
      this.classList.toggle('is-empty', Boolean(isEmptyCart));
      drawer.bindPremiumDrawerUi?.();
    }

    if (noteValue != null) {
      const newNote = document.getElementById('CartDrawer-Note');
      if (newNote) newNote.value = noteValue;
    }
    if (noteOpen) {
      const panel = document.querySelector('[data-cart-drawer-note-panel]');
      const toggle = document.querySelector('[data-cart-drawer-note-toggle]');
      if (panel) panel.removeAttribute('hidden');
      toggle?.setAttribute('aria-expanded', 'true');
    }

    this.scrollTop = scrollTop;
    document.dispatchEvent(new CustomEvent('cart-drawer:dom-updated'));
  }

  formatCartMoney(cents) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return null;
    const format =
      document.querySelector('cart-drawer')?.dataset?.moneyFormat ||
      document.querySelector('cart-sticky-bar')?.dataset?.moneyFormat ||
      '${{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const amount = (cents / 100).toFixed(2);
    return String(format)
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  /** Instant line price feedback while the cart/change request is in flight */
  optimisticUpdateLine(line, quantity) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    if (!row || quantity < 1) return;
    const unit = parseInt(row.dataset.unitPrice, 10);
    if (!Number.isFinite(unit)) return;
    const formatted = this.formatCartMoney(unit * quantity);
    if (!formatted) return;
    row.querySelectorAll('.cart-item__price, .cart-item__totals--sync .price').forEach((el) => {
      el.textContent = formatted;
    });
    row.dataset.lineQuantity = String(quantity);
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const isClear = event?.currentTarget instanceof CartRemoveButton;
    const eventTarget = isClear ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);
    const lineKey = String(line);
    const requestSeq = (this.quantityUpdateSeq[lineKey] || 0) + 1;
    this.quantityUpdateSeq[lineKey] = requestSeq;

    this.clearLineItemError(line);
    if (!isClear && quantity > 0) {
      this.optimisticUpdateLine(line, quantity);
    } else if (isClear || quantity === 0) {
      const row = this.querySelector(`#CartDrawer-Item-${line}`);
      if (row) {
        row.classList.add('is-removing');
        row.style.height = `${row.offsetHeight}px`;
        requestAnimationFrame(() => {
          row.style.height = '0px';
          row.style.opacity = '0';
          row.style.overflow = 'hidden';
          row.style.margin = '0';
          row.style.padding = '0';
        });
      }

      /* Last line removed — show empty state immediately (avoid blank white drawer) */
      const remaining = this.querySelectorAll('.cart-item:not(.is-removing)').length;
      if (remaining === 0) {
        this.classList.add('is-empty');
        document.querySelector('cart-drawer')?.classList.add('is-empty');
      }
    }
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        if (requestSeq !== this.quantityUpdateSeq[lineKey]) return;

        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);

          if (parsedState.errors) {
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.clearLineItemError(line);
            const row = this.querySelector(`#CartDrawer-Item-${line}`);
            if (row) {
              row.classList.remove('is-removing');
              row.style.height = '';
              row.style.opacity = '';
              row.style.overflow = '';
              row.style.margin = '';
              row.style.padding = '';
            }
            this.classList.remove('is-empty');
            document.querySelector('cart-drawer')?.classList.remove('is-empty');
            return;
          }

          /*
            User may have tapped +/- again while this request was in flight.
            Keep their latest intended qty so we don't wipe clicks on DOM refresh.
          */
          const liveQty = quantityElement ? parseInt(quantityElement.value, 10) : NaN;
          const desiredQty = this.desiredQuantityByLine[lineKey];
          const latestQty = Number.isFinite(desiredQty)
            ? desiredQty
            : Number.isFinite(liveQty)
              ? liveQty
              : quantity;
          const needsResync =
            !isClear &&
            quantity > 0 &&
            Number.isFinite(latestQty) &&
            latestQty > 0 &&
            latestQty !== quantity;

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) {
            cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          }

          const drawerHtml = parsedState.sections?.['cart-drawer'];
          if (drawerHtml) {
            this.applyDrawerSectionHtml(drawerHtml);
          }

          if (needsResync) {
            const inputAfter = document.getElementById(`Drawer-quantity-${line}`);
            if (inputAfter) {
              inputAfter.value = String(latestQty);
              this.desiredQuantityByLine[lineKey] = latestQty;
              this.optimisticUpdateLine(line, latestQty);
            }
            this.updateQuantity(line, latestQty, event, name, variantId);
            return;
          }

          if (this.desiredQuantityByLine[lineKey] === quantity) {
            delete this.desiredQuantityByLine[lineKey];
          }

          const iconSection = this.getSectionsToRender().find((s) => s.id === 'cart-icon-bubble');
          if (iconSection && parsedState.sections?.[iconSection.section]) {
            const iconRoot = document.getElementById(iconSection.id);
            const iconEl = iconRoot?.querySelector(iconSection.selector) || iconRoot;
            if (iconEl) {
              iconEl.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[iconSection.section],
                iconSection.selector
              );
            }
          }

          this.clearLineItemError(line);
          this.updateLiveRegions(line, '');

          /* Avoid trapFocus on every +/- — it steals focus and feels laggy */
          if (parsedState.item_count === 0 && cartDrawerWrapper) {
            const emptyFocus =
              cartDrawerWrapper.querySelector('.drawer__inner-empty a') ||
              cartDrawerWrapper.querySelector('.drawer__close');
            if (emptyFocus) {
              trapFocus(
                cartDrawerWrapper.querySelector('.drawer__inner-empty') || cartDrawerWrapper,
                emptyFocus
              );
            }
          }
        });

        if (requestSeq !== this.quantityUpdateSeq[lineKey]) return;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        if (requestSeq !== this.quantityUpdateSeq[lineKey]) return;
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (requestSeq !== this.quantityUpdateSeq[lineKey]) return;
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
